import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { backendStorageSessionsKey } from 'src/app/@store/remote-diagnostics/remote-diagnostics.model';
import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { ClientService } from '../../client.service';

import {
  DataBooleanStatus,
  KeyValueStorage,
  SavedNumberOfInstallations,
} from './vitoguide-backend-settings.model';
import { SettingsPreferences } from '../../../../app/@store';

@Injectable()
export class VitoguideBackendSettingsApi {
  constructor(
    private clientService: ClientService,
    private appInsights: AppInsightsMonitoringService
  ) {}
  /**
   * Returns note based on installation.
   * @param installationId
   */
  getNote(installationId: string): Observable<any> {
    const endpoint = `vitoguide/settings/v1/notes/${installationId}`;

    return this.clientService.get(endpoint);
  }
  /**
   * Adds/Update a note to installation.
   * @param installationId
   * @param note
   */
  createNote(installationId: string, note: string): Observable<any> {
    const endpoint = `vitoguide/settings/v1/notes/${installationId}`;

    return this.clientService.post(endpoint, { note });
  }
  /**
   * Deletes a note from installation.
   * @param installationId
   */
  deleteNote(installationId: string): Observable<any> {
    const endpoint = `vitoguide/settings/v1/notes/${installationId}`;

    return this.clientService.delete(endpoint);
  }

  getSettingsPreferences(): Observable<{
    preferences: SettingsPreferences;
  }> {
    const endpoint = `vitoguide/settings/v1/settings-preferences`;

    return this.clientService.get(endpoint);
  }

  setSettingsPreferences(preferences: SettingsPreferences): Observable<void> {
    const endpoint = `vitoguide/settings/v1/settings-preferences`;

    return this.clientService.post(endpoint, { preferences });
  }

  /**
   * Get key value
   * @param key
   */
  getKeyValueStorage(key: string): Observable<KeyValueStorage> {
    const endpoint = `vitoguide/settings/v1/${key}`;

    if (key === backendStorageSessionsKey) {
      this.appInsights.logEvent(
        APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_SESSIONS_ATTEMPT,
        LogLevelEnum.TRACE
      );
    }

    return this.clientService.get(endpoint);
  }

  /**
   * Set key value
   * @param key
   * @param value
   */
  setKeyValueStorage(key: string, value: KeyValueStorage): Observable<void> {
    const endpoint = `vitoguide/settings/v1/${key}`;

    if (key === backendStorageSessionsKey) {
      this.appInsights.logEvent(
        APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.UPDATE_ACTIVE_SESSIONS_ATTEMPT,
        LogLevelEnum.TRACE,
        { activeSessions: value }
      );
    }

    return this.clientService.post(endpoint, value);
  }

  getNumberOfDisplayedInstallations(): Observable<SavedNumberOfInstallations> {
    const endpoint = `vitoguide/settings/v1/installations-quantity`;

    return this.clientService.get<SavedNumberOfInstallations>(endpoint);
  }

  setNumberOfDisplayedInstallations(numberOfInstallations: number): Observable<void> {
    const endpoint = `vitoguide/settings/v1/installations-quantity`;

    return this.clientService.post<void>(endpoint, { numberOfInstallations });
  }

  getWhatsAppBannerStatus(): Observable<DataBooleanStatus> {
    const endpoint = `vitoguide/settings/v1/whatsapp-banner`;

    return this.clientService.get<DataBooleanStatus>(endpoint);
  }

  setWhatsAppBannerStatus(data: boolean): Observable<DataBooleanStatus> {
    const endpoint = `vitoguide/settings/v1/whatsapp-banner`;
    const payload: DataBooleanStatus = { data };

    return this.clientService.post<DataBooleanStatus>(endpoint, payload);
  }
}
